'use client';
import classNames from 'classnames';
import React, { useId, useState } from 'react';
import { optionKeyOf, type FilterCategory } from './productFilterTypes';

export type FilterGroupProps = {
  category: FilterCategory;
  selected: readonly string[];
  counts?: ReadonlyMap<string, number>;
  /**
   * Values that would still return a product. `undefined` means "not computed"
   * and nothing is greyed — during a quote, availability cannot resolve, so
   * every value stays selectable (CMS-1542 R12.3.4).
   */
  available?: ReadonlySet<string>;
  onToggle: (value: string) => void;
  /** Labels for the show-more / show-less control, from the panel's dictionary. */
  showAllLabel: string;
  showLessLabel: string;
};

/**
 * One checkbox group. The heading toggles the group; `collapseAt` separately
 * truncates the option list, which is why there are two pieces of open state
 * rather than one — a reader can collapse the whole group or just fold the tail.
 */
export const FilterGroup: React.FC<FilterGroupProps> = ({
  category,
  selected,
  counts,
  available,
  onToggle,
  showAllLabel,
  showLessLabel,
}) => {
  const listId = useId();
  const { collapseAt } = category;
  // 0 and undefined both mean "show all": a limit of zero would otherwise hide
  // every option behind a control that says how many are hidden.
  const limit = collapseAt && collapseAt > 0 ? collapseAt : undefined;
  const truncated = limit !== undefined && category.options.length > limit;

  // R13.1: the configured state applies on load only, and the reader's own
  // choice then stands for the visit — so this is state, seeded once, and is
  // deliberately not synced back to the prop on a re-render. Seeded open far
  // enough to show what is ticked: the sheet remounts on every open, and a
  // folded-away tick looks unticked (CMS-2106).
  const [open, setOpen] = useState(
    () => category.defaultExpanded || selected.length > 0,
  );
  const [showAll, setShowAll] = useState(
    () =>
      truncated &&
      category.options
        .slice(limit)
        .some((option) => selected.includes(optionKeyOf(option))),
  );
  const visible =
    truncated && !showAll ? category.options.slice(0, limit) : category.options;

  return (
    <div
      data-component="filter-group"
      className="flex flex-col gap-spacing/gap/y-xs p-4"
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className="mhg-alpha-typography-caption flex w-full items-center justify-between text-left font-bold uppercase tracking-wider text-text/secondary text-xs md:text-sm"
      >
        {category.name}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          className={classNames('size-4 shrink-0 transition-transform', {
            'rotate-180': open,
          })}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* The class carries the state, not the `hidden` attribute: `[hidden]` is
          a preflight base rule and loses to a `flex` utility of equal
          specificity declared later. The attribute stays for the a11y tree. */}
      <div
        id={listId}
        hidden={!open}
        className={classNames(open ? 'flex' : 'hidden', 'flex-col gap-2')}
      >
        {visible.map((option) => {
          const optionKey = optionKeyOf(option);
          const isChecked = selected.includes(optionKey);
          const count = counts?.get(optionKey);
          // Greyed rather than hidden, and still in the DOM with
          // `aria-disabled` so it is announced as unavailable instead of
          // silently ignoring a tap (CMS-1542 R4.4; Genevieve, 3 Sep). A value
          // the reader has already ticked is never greyed — that would strand
          // an active filter they cannot see to remove.
          const isUnavailable =
            !!available && !available.has(optionKey) && !isChecked;

          return (
            <label
              key={optionKey}
              aria-disabled={isUnavailable || undefined}
              className={classNames(
                'mhg-alpha-typography-body-2 flex items-center gap-2 text-sm',
                isUnavailable
                  ? 'cursor-not-allowed text-text/secondary-lighter'
                  : 'cursor-pointer text-text/primary',
              )}
            >
              <input
                type="checkbox"
                checked={isChecked}
                disabled={isUnavailable}
                onChange={() => onToggle(optionKey)}
                className="size-4 shrink-0 rounded-radius/button-inner border-borders/form-default accent-fill/button-primary disabled:cursor-not-allowed"
              />
              <span className="min-w-0 flex-1 truncate">
                {option.label ?? option.value}
              </span>
              {/* Counted against the full product set, so it does not shrink as
                  the reader ticks boxes in this same group. */}
              {count !== undefined && (
                <span className="shrink-0 text-text/secondary">{count}</span>
              )}
            </label>
          );
        })}

        {truncated && (
          <button
            type="button"
            onClick={() => setShowAll((wasShown) => !wasShown)}
            className="mhg-alpha-typography-button-2 self-start text-left text-text/link"
          >
            {showAll
              ? showLessLabel
              : `${showAllLabel} (${category.options.length})`}
          </button>
        )}
      </div>
    </div>
  );
};
